import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useBackend } from '@/composables'

const STORE_NAME = 'dashboardConfigs'

const parseJson = (value, fallback = {}) => {
    try {
        if (typeof value === 'string' && value.trim().length) {
            return JSON.parse(value)
        }
        return typeof value === 'object' && value !== null ? value : fallback
    } catch (err) {
        console.warn('Failed to parse dashboard config json', err)
        return fallback
    }
}

export const useDashboardConfigStore = defineStore(STORE_NAME, () => {
    const configs = ref([])
    const lastLoadedAt = ref(null)
    const loading = ref(false)
    const lastError = ref(null)

    const {
        items,
        item,
        getItems,
        addItem,
        updateItem,
        deleteItem,
        error: backendError
    } = useBackend('settings/configs')

    const {
        item: configByKey,
        getItem: getConfigByKey,
        error: byKeyError
    } = useBackend('settings/configs/by-key')

    const configsByKey = computed(() => {
        return configs.value.reduce((acc, current) => {
            if (!current?.configKey) {
                return acc
            }

            acc[current.configKey] = {
                ...current,
                parsed: parseJson(current.configJson)
            }

            return acc
        }, {})
    })

    const getConfigRecord = (configKey) => configsByKey.value[configKey] || null

    const getConfigValue = (configKey, fallback = null) => {
        return getConfigRecord(configKey)?.parsed ?? fallback
    }

    const setLocalConfig = (configDto) => {
        if (!configDto) {
            return null
        }

        const targetIndex = configs.value.findIndex(cfg => cfg.id === configDto.id)
        if (targetIndex >= 0) {
            configs.value[targetIndex] = configDto
        } else {
            configs.value = [...configs.value, configDto]
        }

        return configDto
    }

    const loadAll = async (force = false) => {
        if (!force && configs.value.length) {
            return
        }

        loading.value = true
        lastError.value = null

        await getItems()

        if (backendError.value) {
            lastError.value = backendError.value
        } else {
            configs.value = items.value ? [...items.value] : []
            lastLoadedAt.value = new Date().toISOString()
        }

        loading.value = false
    }

    const ensureLoaded = async () => {
        if (!configs.value.length && !loading.value) {
            await loadAll()
        }
    }

    const loadByKey = async (configKey) => {
        if (!configKey) {
            return null
        }

        await getConfigByKey(configKey)

        if (byKeyError.value) {
            lastError.value = byKeyError.value
            return null
        }

        // Clear any previous error on successful load
        lastError.value = null

        if (configByKey.value) {
            return setLocalConfig(configByKey.value)
        }

        return null
    }

    const upsertConfig = async (configKey, payload = {}) => {
        const existing = getConfigRecord(configKey)
        const normalizedPayload = {
            configKey,
            configJson: JSON.stringify(payload ?? {})
        }

        try {
            if (existing) {
                item.value = { ...existing, ...normalizedPayload }
                await updateItem(existing.id)
            } else {
                item.value = normalizedPayload
                await addItem(normalizedPayload)
            }

            if (!backendError.value) {
                setLocalConfig(item.value)
            } else {
                lastError.value = backendError.value
            }
        } catch (err) {
            console.error('Failed to persist dashboard config', err)
            lastError.value = err
            throw err
        }

        return getConfigRecord(configKey)
    }

    const deleteConfig = async (configKey) => {
        const existing = getConfigRecord(configKey)
        if (!existing) {
            return
        }

        await deleteItem(existing.id)

        if (!backendError.value) {
            configs.value = configs.value.filter(cfg => cfg.id !== existing.id)
        } else {
            lastError.value = backendError.value
        }
    }

    const reset = () => {
        configs.value = []
        lastLoadedAt.value = null
        lastError.value = null
    }

    return {
        configs,
        configsByKey,
        lastLoadedAt,
        loading,
        lastError,
        loadAll,
        ensureLoaded,
        loadByKey,
        getConfigRecord,
        getConfigValue,
        upsertConfig,
        deleteConfig,
        reset
    }
}, {
    persist: true
})
